import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Sparkles, X, BookMarked } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CHAPTERS_KEY = 'lumera_guest_chapters';       // JSON array of unique "book/chapter" keys
const GATED_KEY = 'lumera_guest_gated_opens';       // integer count of gated-tool taps
const VERSE_ACTION_KEY = 'lumera_guest_verse_acts';  // integer count of highlight/copy attempts
const DISMISS_KEY = 'lumera_guest_nudge_dismissed';
const SESSION_KEY = 'lumera_session_id';

// Thresholds that signal genuine intent.
const CHAPTER_THRESHOLD = 3;
const GATED_THRESHOLD = 2;
const VERSE_ACTION_THRESHOLD = 1; // a highlight/copy is a strong, immediate signal

const getSessionId = () => {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (crypto?.randomUUID?.() || String(Date.now() + Math.random()));
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch { return null; }
};

const logNudge = (event, trigger) => {
  try {
    axios.post(`${BACKEND_URL}/api/analytics/nudge`, {
      event, trigger: trigger || 'unknown', session_id: getSessionId(),
    }).catch(() => { /* fire-and-forget */ });
  } catch { /* ignore */ }
};

const bump = (key) => {
  try {
    const n = parseInt(localStorage.getItem(key) || '0', 10) + 1;
    localStorage.setItem(key, String(n));
    window.dispatchEvent(new Event('lumera-guest-activity'));
  } catch { /* ignore */ }
};

/** Called from useAuthGate when a guest taps a generic gated feature. */
export const recordGuestGatedOpen = () => bump(GATED_KEY);

/** Called when a guest attempts a strong verse action (highlight/copy). */
export const recordGuestVerseAction = () => bump(VERSE_ACTION_KEY);

const readChapters = () => {
  try { return JSON.parse(localStorage.getItem(CHAPTERS_KEY) || '[]'); }
  catch { return []; }
};

/**
 * Guest "save your progress" nudge — a one-time, dismissible bottom banner
 * shown after a guest reads 3+ chapters, opens a gated tool twice, or attempts
 * a verse highlight/copy. Fully client-side; renders nothing for signed-in
 * users. Fires funnel analytics (shown/clicked/dismissed + trigger).
 */
const GuestUpgradeNudge = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const es = language === 'es';
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef('unknown');
  const shownLoggedRef = useRef(false);

  const resolveTrigger = useCallback(() => {
    const verseActs = parseInt(localStorage.getItem(VERSE_ACTION_KEY) || '0', 10);
    const gated = parseInt(localStorage.getItem(GATED_KEY) || '0', 10);
    const chapters = readChapters().length;
    if (verseActs >= VERSE_ACTION_THRESHOLD) return 'verse_action';
    if (gated >= GATED_THRESHOLD) return 'gated';
    if (chapters >= CHAPTER_THRESHOLD) return 'chapters';
    return null;
  }, []);

  const evaluate = useCallback(() => {
    if (localStorage.getItem(DISMISS_KEY) === '1') return;
    const trigger = resolveTrigger();
    if (trigger) {
      triggerRef.current = trigger;
      setVisible(true);
    }
  }, [resolveTrigger]);

  // Track distinct chapters a guest reads via /bible/:book/:chapter navigations.
  useEffect(() => {
    if (user) return;
    const m = location.pathname.match(/^\/bible\/([^/]+)\/([^/]+)/);
    if (m) {
      const key = `${m[1]}/${m[2]}`;
      const seen = readChapters();
      if (!seen.includes(key)) {
        seen.push(key);
        try { localStorage.setItem(CHAPTERS_KEY, JSON.stringify(seen)); } catch { /* ignore */ }
      }
    }
    evaluate();
  }, [location.pathname, user, evaluate]);

  // React to gated-tool / verse-action taps fired from useAuthGate & the reader.
  useEffect(() => {
    if (user) return;
    const onActivity = () => evaluate();
    window.addEventListener('lumera-guest-activity', onActivity);
    return () => window.removeEventListener('lumera-guest-activity', onActivity);
  }, [user, evaluate]);

  // Log a single "shown" impression per appearance.
  useEffect(() => {
    if (visible && !shownLoggedRef.current) {
      shownLoggedRef.current = true;
      logNudge('shown', triggerRef.current);
    }
  }, [visible]);

  const onSignup = () => {
    logNudge('clicked', triggerRef.current);
    navigate('/login');
  };

  const dismiss = () => {
    logNudge('dismissed', triggerRef.current);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setVisible(false);
  };

  if (user || !visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 sm:px-4 sm:pb-4 pointer-events-none"
      data-testid="guest-upgrade-nudge"
    >
      <div className="pointer-events-auto mx-auto max-w-2xl rounded-2xl border border-amber-300/60 dark:border-amber-700/60 bg-white/95 dark:bg-slate-900/95 backdrop-blur shadow-xl shadow-amber-900/10 p-4 flex items-center gap-4">
        <div className="hidden sm:flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
          <BookMarked className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-slate-900 dark:text-slate-100">
            {es ? '¿Disfrutando la lectura?' : 'Enjoying your study?'}
          </p>
          <p className="text-xs text-muted-foreground">
            {es
              ? 'Crea una cuenta gratis para guardar resaltados, notas y tu progreso.'
              : 'Create a free account to save highlights, notes, and your progress.'}
          </p>
        </div>
        <Button
          onClick={onSignup}
          size="sm"
          className="flex-shrink-0 bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="guest-nudge-signup"
        >
          <Sparkles className="mr-1.5 h-4 w-4" />
          {es ? 'Registrarse' : 'Sign up — free'}
        </Button>
        <button
          onClick={dismiss}
          className="flex-shrink-0 p-1 rounded-full text-muted-foreground hover:bg-accent"
          aria-label={es ? 'Descartar' : 'Dismiss'}
          data-testid="guest-nudge-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default GuestUpgradeNudge;
